/**
 * Duplikat-Bereinigung für Mitarbeiter (nur Verwalter).
 * Findet mögliche Doppel-Datensätze, lässt pro Gruppe den zu behaltenden MA
 * auswählen und führt zusammen (leere Felder auffüllen + Verknüpfungen umhängen
 * + Duplikate löschen).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Loader2, Users, GitMerge, CheckCircle2, AlertTriangle, Calendar, Clock, MapPin, KeyRound,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { employeesAPI } from '../lib/api';
import { toast } from 'sonner';

// Deutsche Labels für ergänzte Felder & umgehängte Verknüpfungen (Merge-Protokoll)
const FIELD_LABELS = {
  email: 'E-Mail', phone: 'Telefon', mobile: 'Mobil', date_of_birth: 'Geburtsdatum',
  place_of_birth: 'Geburtsort', gender: 'Geschlecht', nationality: 'Nationalität',
  street: 'Straße', house_number: 'Hausnummer', postal_code: 'PLZ', city: 'Ort',
  country: 'Land', position: 'Position', department: 'Abteilung', location_id: 'Standort',
  employment_start: 'Eintrittsdatum', employment_end: 'Austrittsdatum',
  employment_type: 'Beschäftigungsart', working_time_model: 'Arbeitszeitmodell',
  weekly_hours: 'Wochenstunden', vacation_days_per_year: 'Urlaubstage/Jahr',
  business_email: 'Dienstl. E-Mail', business_phone: 'Dienstl. Telefon', business_mobile: 'Dienstl. Mobil',
  iban: 'IBAN', bic: 'BIC', bank_name: 'Bank', notes: 'Notizen', user_id: 'Login-Zugang',
  qualifications: 'Qualifikationen', certifications: 'Zertifikate', trainings: 'Weiterbildungen',
  emergency_contact: 'Notfallkontakt', taetigkeitsart: 'Tätigkeitsart',
};
const COLL_LABELS = {
  shifts: 'Schichten', 'shifts.assigned_to': 'Schicht-Zuordnungen', shift_assignments: 'Schicht-Zuordnungen',
  time_entries: 'Zeiteinträge', time_corrections: 'Zeitkorrekturen', absences: 'Abwesenheiten',
  vacations: 'Urlaube', vacation_requests: 'Urlaubsanträge', employee_notes: 'Notizen',
  it_access: 'IT-Zugänge', monthly_working_hours: 'Monatsstunden', qualification_proposals: 'Qualifikations-Anträge',
  personaleinsatz: 'Personaleinsätze', change_requests: 'Änderungsanträge', room_bookings: 'Raumbuchungen',
  workflow_instances: 'Workflows', facility_tickets: 'Haustechnik-Tickets', 'facility_tickets.assigned_to': 'Haustechnik-Zuordnungen',
};
const fieldLabel = (k) => FIELD_LABELS[k] || k;
const collLabel = (k) => COLL_LABELS[k] || k;

export const DuplicateCleanupDialog = ({ open, onOpenChange, onChanged }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});   // group_id -> keep_id
  const [confirmGroup, setConfirmGroup] = useState(null);
  const [busyGroup, setBusyGroup] = useState(null);
  const [results, setResults] = useState({});     // group_id -> merge result (Protokoll)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeesAPI.findDuplicates();
      const gs = res.data?.groups || [];
      setGroups(gs);
      setResults({});
      const sel = {};
      gs.forEach((g) => { sel[g.group_id] = g.suggested_keep_id; });
      setSelected(sel);
    } catch { toast.error('Duplikate konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { load(); setConfirmGroup(null); } }, [open, load]);

  const fmtDate = (d) => { if (!d) return '—'; try { return format(parseISO(d), 'dd.MM.yyyy', { locale: de }); } catch { return d; } };

  const mergeGroup = async (group) => {
    const keepId = selected[group.group_id];
    const removeIds = group.members.filter((m) => m.id !== keepId).map((m) => m.id);
    if (!keepId || removeIds.length === 0) return;
    setBusyGroup(group.group_id);
    try {
      const res = await employeesAPI.mergeDuplicates(keepId, removeIds);
      const keepMember = group.members.find((m) => m.id === keepId);
      toast.success(`Zusammengeführt: ${res.data.deleted} gelöscht, ${res.data.fields_filled.length} Felder ergänzt`);
      setResults((prev) => ({ ...prev, [group.group_id]: { ...res.data, keep_name: keepMember?.full_name } }));
      setConfirmGroup(null);
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Zusammenführen fehlgeschlagen'); }
    finally { setBusyGroup(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="duplicate-cleanup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Duplikate bereinigen</DialogTitle>
          <DialogDescription>
            Mögliche Doppel-Mitarbeiter (gleicher Name + Geburtsdatum, E-Mail oder Personalnummer).
            Wähle je Gruppe den zu behaltenden Datensatz. Leere Felder werden ergänzt, Verknüpfungen umgehängt.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="no-duplicates">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
            <p className="text-sm">Keine Duplikate gefunden.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{groups.length} Gruppe(n) gefunden.</p>
            {groups.map((g) => {
              const keepId = selected[g.group_id];
              const isConfirming = confirmGroup === g.group_id;
              const removeCount = g.members.length - 1;
              const result = results[g.group_id];
              if (result) {
                const reassignedEntries = Object.entries(result.reassigned || {});
                const totalReassigned = reassignedEntries.reduce((s, [, n]) => s + n, 0);
                return (
                  <div key={g.group_id} className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid={`dup-result-${g.group_id}`}>
                    <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                      <CheckCircle2 className="h-4 w-4" />
                      Zusammengeführt in {result.keep_name} · {result.deleted} Duplikat(e) gelöscht
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1 pl-6">
                      <div>
                        <span className="font-medium text-foreground">Ergänzte Felder:</span>{' '}
                        {result.fields_filled?.length
                          ? result.fields_filled.map(fieldLabel).join(', ')
                          : 'keine (behaltener Datensatz war vollständig)'}
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Umgehängte Verknüpfungen:</span>{' '}
                        {totalReassigned
                          ? reassignedEntries.map(([k, n]) => `${collLabel(k)}: ${n}`).join(' · ')
                          : 'keine'}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div key={g.group_id} className="rounded-lg border border-border p-3 space-y-2" data-testid={`dup-group-${g.group_id}`}>
                  <div className="text-sm font-semibold">{g.members[0]?.full_name}</div>
                  <div className="space-y-2">
                    {g.members.map((m) => {
                      const isKeep = m.id === keepId;
                      return (
                        <label
                          key={m.id}
                          className={`flex items-start gap-3 rounded-md border p-2.5 cursor-pointer transition-colors ${isKeep ? 'border-primary bg-primary/5' : 'border-border'}`}
                          data-testid={`dup-member-${m.id}`}
                        >
                          <input
                            type="radio"
                            name={`keep-${g.group_id}`}
                            className="mt-1 h-4 w-4"
                            checked={isKeep}
                            disabled={busyGroup === g.group_id}
                            onChange={() => setSelected((p) => ({ ...p, [g.group_id]: m.id }))}
                            data-testid={`dup-keep-radio-${m.id}`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium">{m.full_name}</span>
                              <Badge variant="outline" className="text-[10px]">{m.employee_number || 'ohne Nr.'}</Badge>
                              {isKeep
                                ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">behalten</Badge>
                                : <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">wird gelöscht</Badge>}
                              {m.has_login && <Badge variant="secondary" className="text-[10px]"><KeyRound className="h-3 w-3 mr-0.5" />Login</Badge>}
                              {!m.is_active && <Badge variant="outline" className="text-[10px]">inaktiv</Badge>}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground mt-1">
                              {m.email && <span>{m.email}</span>}
                              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(m.date_of_birth)}</span>
                              {m.location_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{m.location_name}</span>}
                              <span className="flex items-center gap-1"><Users className="h-3 w-3" />{m.shift_count} Schichten</span>
                              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{m.time_entry_count} Zeiteinträge</span>
                              <span>angelegt {fmtDate(m.created_at)}</span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {isConfirming ? (
                    <div className="flex items-center justify-end gap-2 bg-amber-50 border border-amber-200 rounded-md p-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <span className="text-xs text-amber-800 mr-auto">{removeCount} Duplikat(e) endgültig löschen und zusammenführen?</span>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmGroup(null)} disabled={busyGroup === g.group_id} data-testid={`dup-cancel-${g.group_id}`}>Abbrechen</Button>
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => mergeGroup(g)} disabled={busyGroup === g.group_id} data-testid={`dup-confirm-merge-${g.group_id}`}>
                        {busyGroup === g.group_id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <GitMerge className="h-3.5 w-3.5 mr-1.5" />}
                        Ja, zusammenführen
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={() => setConfirmGroup(g.group_id)} data-testid={`dup-merge-btn-${g.group_id}`}>
                        <GitMerge className="h-3.5 w-3.5 mr-1.5" /> Zusammenführen
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
